// Palette and reusable style fragments for the Relic Vault, taken from the
// designer's handoff (store.css / vault.css).

import { FONTS, IS_WEB, webOnly } from 'utils/relicVault/webStyle';

export const COLORS = {
  accent: '#8B5CF6',
  accentDeep: '#5B2FD9',
  text: '#F2EFFC',
  textWarm: '#F5F1E4',
  textBright: '#F8F6FF',
  lilac: '#B9A6FF',
  lilacSoft: '#C9C1F0',
  lilacLight: '#D6C8FF',
  muted: '#9E97C2',
  dim: '#6B6390',
  gold: '#FFD873',
  goldDeep: '#F0A93A',
  green: '#4ADE80',
  red: '#F87171',
  page: '#0A0918',
  modalTop: 'rgba(54,42,110,0.98)',
  modalBottom: 'rgba(18,15,42,0.98)',
};

// The store's text is on the type standard (Sol mobile-type-audit D2 A, 15 Oct): Plus Jakarta Sans at 400/600/700 only -
// the handoff's 500/800/900 land on the nearest standard weight, and Poppins is kept for the store's page title alone.
const STANDARD_WEIGHT = { 400: '400', 500: '600', 600: '600', 700: '700', 800: '700', 900: '700' };
const standardWeight = weight => STANDARD_WEIGHT[weight] || '400';

export const headingFont = weight => ({
  fontFamily: FONTS.body,
  fontWeight: standardWeight(weight),
});

export const bodyFont = weight => ({
  fontFamily: FONTS.body,
  fontWeight: standardWeight(weight),
});

/** letter-spacing in em -> px for a given font size. */
export const em = (value, fontSize) => value * fontSize;

/** Soft gradient panel used by .panel, .vault-sidebar and .vault-header. */
export const panelSurface = {
  backgroundColor: 'rgba(34,27,75,0.55)',
  ...webOnly({
    backgroundImage: 'linear-gradient(180deg, rgba(48,38,102,0.55), rgba(20,17,48,0.55))',
  }),
};

/** Purple modal card surface used by both popups. */
export const modalSurface = {
  backgroundColor: 'rgba(36,28,76,0.98)',
  ...webOnly({
    backgroundImage: `linear-gradient(180deg, ${COLORS.modalTop}, ${COLORS.modalBottom})`,
    boxShadow: '0 30px 80px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.06)',
  }),
};

/** Primary purple button gradient (.buy-btn.buy, .modal-btn.confirm). */
export const accentButton = (shadow = '0 4px 16px rgba(139,92,246,0.45)') => ({
  backgroundColor: COLORS.accent,
  ...webOnly({
    backgroundImage: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accentDeep})`,
    boxShadow: shadow,
  }),
});

/** Page backdrop: dot grid + purple and green glows over deep navy. */
export const pageBackground = {
  backgroundColor: COLORS.page,
  ...webOnly({
    backgroundImage: [
      'radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)',
      'radial-gradient(ellipse 900px 500px at 15% -5%, rgba(139,92,246,0.28), transparent 60%)',
      'radial-gradient(ellipse 800px 500px at 100% 10%, rgba(76,222,128,0.10), transparent 55%)',
      'linear-gradient(180deg, #0C0A20 0%, #0A0918 100%)',
    ].join(', '),
    backgroundSize: '28px 28px, auto, auto, auto',
  }),
};

/** Backdrop behind popups: dimmed + blurred world. */
export const modalBackdrop = {
  backgroundColor: 'rgba(6,5,18,0.72)',
  ...webOnly({ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }),
};

/**
 * Per-rarity border + glow for store relic cards (.rarity-<key>). Mythic uses a
 * layered rainbow glow.
 */
export const RARITY_CARD = {
  common: {
    border: 'rgba(74,222,128,0.55)',
    borderHover: 'rgba(74,222,128,0.75)',
    shadow: '0 0 0 1px rgba(74,222,128,0.30), 0 0 18px rgba(74,222,128,0.28), inset 0 0 20px rgba(74,222,128,0.06)',
    shadowHover: '0 0 0 1px rgba(74,222,128,0.55), 0 0 26px rgba(74,222,128,0.42), inset 0 0 20px rgba(74,222,128,0.08)',
  },
  uncommon: {
    border: 'rgba(56,189,248,0.55)',
    borderHover: 'rgba(56,189,248,0.75)',
    shadow: '0 0 0 1px rgba(56,189,248,0.30), 0 0 18px rgba(56,189,248,0.28), inset 0 0 20px rgba(56,189,248,0.06)',
    shadowHover: '0 0 0 1px rgba(56,189,248,0.55), 0 0 26px rgba(56,189,248,0.42), inset 0 0 20px rgba(56,189,248,0.08)',
  },
  rare: {
    border: 'rgba(167,139,250,0.55)',
    borderHover: 'rgba(167,139,250,0.75)',
    shadow: '0 0 0 1px rgba(167,139,250,0.30), 0 0 18px rgba(167,139,250,0.28), inset 0 0 20px rgba(167,139,250,0.06)',
    shadowHover: '0 0 0 1px rgba(167,139,250,0.55), 0 0 26px rgba(167,139,250,0.42), inset 0 0 20px rgba(167,139,250,0.08)',
  },
  epic: {
    border: 'rgba(239,68,68,0.55)',
    borderHover: 'rgba(239,68,68,0.75)',
    shadow: '0 0 0 1px rgba(239,68,68,0.30), 0 0 18px rgba(239,68,68,0.28), inset 0 0 20px rgba(239,68,68,0.06)',
    shadowHover: '0 0 0 1px rgba(239,68,68,0.55), 0 0 26px rgba(239,68,68,0.42), inset 0 0 20px rgba(239,68,68,0.08)',
  },
  legendary: {
    border: 'rgba(251,191,36,0.6)',
    borderHover: 'rgba(251,191,36,0.8)',
    shadow: '0 0 0 1px rgba(251,191,36,0.35), 0 0 22px rgba(251,191,36,0.4), inset 0 0 22px rgba(251,191,36,0.08)',
    shadowHover: '0 0 0 1px rgba(251,191,36,0.65), 0 0 30px rgba(251,191,36,0.55), inset 0 0 22px rgba(251,191,36,0.1)',
  },
  mythic: {
    border: 'rgba(244,114,182,0.6)',
    borderHover: 'rgba(244,114,182,0.85)',
    shadow: '0 0 0 1px rgba(244,114,182,0.35), 0 0 12px rgba(255,90,90,0.4), 0 0 20px rgba(255,205,60,0.35), 0 0 28px rgba(80,190,255,0.35), 0 0 36px rgba(170,100,255,0.35)',
    shadowHover: '0 0 0 1px rgba(244,114,182,0.7), 0 0 16px rgba(255,90,90,0.5), 0 0 26px rgba(255,205,60,0.45), 0 0 34px rgba(80,190,255,0.45), 0 0 44px rgba(170,100,255,0.45)',
  },
};

/** "Mythic" label: rainbow gradient text on web, pink on native. */
export const mythicTextStyle = IS_WEB
  ? {
      backgroundImage: 'linear-gradient(90deg, #F472B6, #FBBF24, #4ADE80, #38BDF8, #A78BFA)',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      color: 'transparent',
    }
  : { color: '#F472B6' };

/** Web-only CSS transition, for hover lifts that CSS animated in the design. */
export const hoverTransition = (ms = 150) =>
  webOnly({
    transitionProperty: 'transform, border-color, background-color, box-shadow, filter',
    transitionDuration: `${ms}ms`,
    transitionTimingFunction: 'ease',
  });
